import { useEffect, useRef } from 'react';

import { Slide, toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { MdBarChart, MdOutlineWifiOff } from 'react-icons/md';
import { GoGear } from 'react-icons/go';
import { CgArrowUpR } from 'react-icons/cg';

import NotificationMessage from '@src/components/NotificationMessage';

import { useRoomContext } from '@src/contexts';

import { useChatStore, useDrawerStore, useNotificationStore, useRoomActionsStore, useRoomStore } from '@src/stores';

import {
  MuteIcon,
  QuestionAndAnswerIcon,
  RecordingIcon,
  UserTypeIcon,
  WhiteRaisedHand,
  MeetingEndIcon,
  CalculatorLogo,
} from '@src/assets/icons';

import { EMessageType, IMessage } from '@src/types';

const useNotificationsHandler = (isDocumentPIP = false) => {
  const { notifications, messagesNotifications, persistentNotification, markAllAsShown, resetNotifications } =
    useNotificationStore(
      useShallow((state) => ({
        notifications: state.notifications,
        messagesNotifications: state.messagesNotifications,
        persistentNotification: state.persistentNotification,
        markAllAsShown: state.markAllAsShown,
        resetNotifications: state.resetNotifications,
      }))
    );

  const { isJoinedRoom } = useRoomStore(
    useShallow((state) => ({
      isJoinedRoom: state.isJoinedRoom,
    }))
  );

  const { isDrawerOpen, selectedKey, setSelectedKey, setActivePollView } = useDrawerStore(
    useShallow((state) => ({
      isDrawerOpen: state.isDrawerOpen,
      selectedKey: state.selectedKey,
      setSelectedKey: state.setSelectedKey,
      setActivePollView: state.setActivePollView,
    }))
  );

  const { isMobileDevice, hasActiveLimitedPolls } = useRoomContext();

  const { setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({
      setSelectedReceiverData: state.setSelectedReceiverData,
    }))
  );

  const isPipActive = useRoomActionsStore(useShallow((state) => state.pipSettings.isActive));
  const prevIsPipActive = useRef(isPipActive);

  // Local set to track notification IDs that should be suppressed
  const suppressedIdsRef = useRef<Set<string>>(new Set());

  // Detect PiP transition and capture "stale" notifications to avoid a burst in the main window
  useEffect(() => {
    if (!isDocumentPIP && prevIsPipActive.current && !isPipActive) {
      // Return from PiP: Capture all current IDs and skip them for toasting
      const staleIds = new Set<string>();
      [...messagesNotifications.values()].forEach((messages) => {
        messages.forEach((msg) => {
          if (msg.chatMessageId) staleIds.add(msg.chatMessageId);
        });
      });
      notifications.forEach((n) => {
        // Don't suppress actionable notifications (requestId) or share-screen - they need user action
        if (n.requestId) return;

        const id = n.requestId || n.peerId || n.text;
        staleIds.add(id);
      });
      suppressedIdsRef.current = staleIds;
    }

    // Clear suppressed IDs when entering PiP so notifications can show again when returning
    if (!isDocumentPIP && !prevIsPipActive.current && isPipActive) {
      suppressedIdsRef.current = new Set();
    }

    prevIsPipActive.current = isPipActive;
  }, [isPipActive, isDocumentPIP, messagesNotifications, notifications]);

  useEffect(() => {
    if (!isJoinedRoom) {
      toast.dismiss();
      return;
    }

    if (isPipActive && !isDocumentPIP) {
      return;
    }

    // Suppress chat toasts in the main window if the chat drawer is already open
    if (!isDocumentPIP && isDrawerOpen && selectedKey === 'chat') {
      return;
    }

    const presentedKeys: { [key: string]: boolean } = {};

    function getReceiverData(message: IMessage) {
      if (message.type === EMessageType.PUBLIC) {
        return { key: EMessageType.PUBLIC, value: EMessageType.PUBLIC };
      }
      if (message.type === EMessageType.PANELIST) {
        return { key: EMessageType.PANELIST, value: EMessageType.PANELIST };
      }
      return { key: EMessageType.PRIVATE, value: message.peer_id };
    }

    [...messagesNotifications.values()].forEach((messages) => {
      messages.forEach((message) => {
        const msgId = message.chatMessageId || '';
        // Only toast if not shown, and NOT suppressed by a PiP return
        if (!message.isShown && !suppressedIdsRef.current.has(msgId)) {
          presentedKeys[msgId] = true;
          const toastId = toast(
            <NotificationMessage
              message={message.peer_msg ? message.peer_msg : 'Just sent a file'}
              peerName={message.peer_name ?? ''}
              peerId={message.peer_id ?? ''}
              isPip={isDocumentPIP}
              onClick={() => {
                if (!hasActiveLimitedPolls) setSelectedKey('chat', true);
                const receiver = getReceiverData(message);
                setSelectedReceiverData(receiver);

                toast.dismiss(toastId);
              }}
            />,
            {
              position: isMobileDevice ? 'bottom-left' : 'bottom-right',
              className: 'toast toast-notification',
              hideProgressBar: true,
              transition: Slide,
              autoClose: 3000,
              closeButton: !isMobileDevice,
              style: {
                marginLeft: isMobileDevice ? '16px' : '0',
                marginBottom: '9px',
              },
              containerId: isDocumentPIP ? 'pip-notification-container' : undefined,
            }
          );
        }
      });
    });

    if (Object.keys(presentedKeys).length) {
      // to not duplicate the notifications we need to set isPresented true.
      markAllAsShown(presentedKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesNotifications, isPipActive, isDocumentPIP, isDrawerOpen, selectedKey]);

  useEffect(() => {
    if (!isJoinedRoom) {
      toast.dismiss();
      return;
    }

    if (isPipActive && !isDocumentPIP && !notifications.some((n) => !!n.requestId)) {
      return;
    }

    notifications.forEach(({ source, text, title, requestId, id, peerId, icon }) => {
      // Show ONLY notifications that have a requestId (meaning they have Allow/Reject buttons) if in main window while PiP is active
      if (isPipActive && !isDocumentPIP && !requestId) {
        return;
      }

      const notificationId = requestId || peerId || text;
      // Skip if suppressed (e.g. already shown in other view when switching PiP/main)
      if (suppressedIdsRef.current.has(notificationId)) {
        return;
      }

      let notificationIcon;
      switch (source) {
        case 'polls':
          notificationIcon = <MdBarChart size={21} color="#00ACFB" />;
          break;
        case 'raised-hand':
          notificationIcon = <WhiteRaisedHand />;
          break;
        case 'recording':
          notificationIcon = <RecordingIcon />;
          break;
        case 'user-type':
          notificationIcon = <UserTypeIcon />;
          break;
        case 'share-screen':
          notificationIcon = (
            <div className="screen-share-notification-icon">
              <CgArrowUpR />
            </div>
          );
          break;
        case 'meeting-end-soon':
          notificationIcon = <MeetingEndIcon />;
          break;
        case 'settings':
          notificationIcon = (
            <GoGear className="notification-gear" height={16} width={16} color={'var(--icon-color)'} />
          );
          break;
        case 'mute':
          notificationIcon = <MuteIcon />;
          break;
        case 'question-and-answer':
          notificationIcon = (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#00acfbff',
                borderRadius: '10px',
                width: '32px',
                height: '32px',
              }}
            >
              <QuestionAndAnswerIcon color="white" width="20px" height="17px" />
            </div>
          );
          break;
        case 'calculator':
          notificationIcon = <CalculatorLogo />;
          break;
        default:
          notificationIcon = icon || null;
          break;
      }
      const toastId = toast(
        <NotificationMessage
          message={source === 'raised-hand' ? 'raised a hand' : text}
          title={title ?? ''}
          peerId={peerId}
          peerName={source === 'raised-hand' ? title : ''}
          titleColor={['polls', 'meeting-end-soon', 'question-and-answer'].includes(source!) ? 'blue' : 'white'}
          icon={notificationIcon}
          notificationLayout={source === 'raised-hand' ? 'inline' : 'message'}
          requestId={requestId}
          isPip={isDocumentPIP}
          onClick={() => {
            if (source === 'polls') {
              setSelectedKey('polls', true);

              if (id === 'newVote') {
                setActivePollView('view');
              }
            }
            toast.dismiss(toastId);
          }}
        />,
        {
          position:
            source === 'share-screen' && !!requestId
              ? isDocumentPIP
                ? 'bottom-right'
                : 'top-center'
              : isMobileDevice
                ? 'bottom-left'
                : 'bottom-right',
          className: 'toast toast-notification',
          hideProgressBar: true,
          transition: Slide,
          autoClose: !!requestId ? false : 3000,
          closeButton: !isMobileDevice && !(source === 'share-screen' && !!requestId),
          style: {
            marginLeft: isMobileDevice ? '16px' : '0',
            marginBottom: '9px',
          },
          toastId: isDocumentPIP ? `${notificationId}-pip` : notificationId,
          containerId: isDocumentPIP ? 'pip-notification-container' : undefined,
        }
      );
      // Don't add requestId, "raised-hand", or "mute" notifications to suppressedIds - they should be able to repeat
      if (!requestId && source !== 'raised-hand' && source !== 'mute') {
        suppressedIdsRef.current.add(notificationId);
      }
    });
    notifications.length && !isDocumentPIP && resetNotifications();
  }, [
    isJoinedRoom,
    isMobileDevice,
    notifications,
    resetNotifications,
    setActivePollView,
    setSelectedKey,
    isPipActive,
    isDocumentPIP,
  ]);

  useEffect(() => {
    if (!isJoinedRoom || !isDocumentPIP) return;

    const PERSISTENT_TOAST_ID = 'persistent-notification-pip';

    if (persistentNotification) {
      toast(
        <NotificationMessage
          message={persistentNotification.text}
          title={persistentNotification.title}
          titleColor="white"
          icon={<MdOutlineWifiOff size={24} color="#EF4444" />}
          notificationLayout="message"
          isPip={isDocumentPIP}
          isConnectionNotification={true}
          onClick={() => {}}
        />,
        {
          toastId: PERSISTENT_TOAST_ID,
          position: isMobileDevice ? 'bottom-left' : 'bottom-right',
          className: 'toast toast-notification',
          hideProgressBar: true,
          transition: Slide,
          autoClose: false,
          closeButton: false,
          closeOnClick: false,
          draggable: false,
          style: {
            marginLeft: isMobileDevice ? '16px' : '0',
            marginBottom: '9px',
          },
          containerId: 'pip-notification-container',
        }
      );
    } else {
      toast.dismiss(PERSISTENT_TOAST_ID);
    }
  }, [persistentNotification, isJoinedRoom, isDocumentPIP, isMobileDevice]);
};

export default useNotificationsHandler;
